import {
  Alert,
  AlertTitle,
  Collapse,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AssessmentCard } from '../../shared';
import { rules } from '../../demographics/rules';
import { MedicationPharmacyFormValues } from '.';
import { DialogBox } from '../../shared/dialog-box';

type PropTypes = {
  form: UseFormReturn<MedicationPharmacyFormValues>;
  onOptionChange: (option: string) => void;
};

const OpenToMedicationQuestion = ({ form, onOptionChange }: PropTypes) => {
  const radioOptions = ['Yes', 'No'];

  const whyWeAskDialog = (
    <>
      <Typography sx={{ mb: 3 }}>
        Our doctors specialize in combining medication management and supportive
        therapy.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        We practice responsible prescribing and only recommend the care you
        actually need.
      </Typography>
      <Typography>
        We don’t offer talk therapy as a standalone service.
      </Typography>
    </>
  );

  const psychiatryVsTherapyDialog = (
    <>
      <Typography sx={{ mb: 3 }}>
        Psychiatry and therapy can both be effective ways to treat mental health
        conditions and, often, they’re even more effective when combined.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        Psychiatrists, and psychiatric nurse practitioners, treat mental health
        conditions with medications designed to help ease symptoms and help
        patients find balance.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        Therapy is a conversation-based approach that gives patients time and
        space to better understand their thoughts, emotions, and behaviors.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        As a psychiatry practice, we specialize in medication management and
        only see patients who would benefit from this approach. We also have
        therapists on staff to work with patients whose psychiatrists believe
        they could benefit from seeing a psychiatrist-therapist team.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        If you’re not sure which approach is right for you, that’s why we’re
        here. We recommend scheduling a visit with a psychiatrist. They’ll be
        able to walk you through your options and recommend the best care, even
        if it’s not with us.
      </Typography>
    </>
  );

  const talkiatryAndMedicationDialog = (
    <>
      <Typography sx={{ mb: 3 }}>
        Our psychiatrists specialize in medication management, but their true
        focus is on the health and wellness of their patients.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        We practice responsible prescribing and only recommend the treatment you
        truly need. If you’re not sure if you’d benefit from medication, or
        aren’t sure, that’s ok.
      </Typography>
      <Typography sx={{ mb: 3 }}>
        The most effective treatment plan is the one you’ll can stick to. Our
        doctors can talk through your options and find a treatment plan that
        fits you.
      </Typography>
    </>
  );

  const { control, watch } = form;
  const isNotOpenToMedication = watch('openToMedication') === 'No';

  return (
    <AssessmentCard>
      <Typography component="h3" variant="h5">
        Are you open to taking medication for your mental health?
      </Typography>
      <DialogBox title="Why we ask" content={whyWeAskDialog} />
      <Controller
        name="openToMedication"
        control={control}
        rules={rules.requiredCheckboxOrRadio as any}
        render={({ field, fieldState }) => (
          <FormControl error={!!fieldState.error}>
            <RadioGroup {...field}>
              {radioOptions.map((option, index) => (
                <FormControlLabel
                  control={<Radio />}
                  key={`assessment-radio-key${index}`}
                  label={option}
                  value={option}
                  onChange={() => onOptionChange(option)}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />
      <Collapse in={isNotOpenToMedication} unmountOnExit>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>We're sorry, we don't offer therapy alone</AlertTitle>
          Our psychiatrists provide medication management combined with
          supportive therapy.
        </Alert>
        <br />
        <DialogBox
          title="Psychiatry vs. therapy"
          content={psychiatryVsTherapyDialog}
        />
        <DialogBox
          title="Talkiatry and medication"
          content={talkiatryAndMedicationDialog}
        />
      </Collapse>
    </AssessmentCard>
  );
};

export default OpenToMedicationQuestion;
